import { useQueryClient } from '@tanstack/react-query';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { canDeleteTimetable, deleteTimetable, isTimetableNotDeletableError } from 'src/api/deleteTimetable';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useDeleteEntityPrompt } from 'src/hooks/useDeleteEntityPrompt';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';

interface UseDeleteTimetablePromptArgs {
  timetableId: string;
  timetableName?: string;
  genericErrorMessage: string;

  onDeleted?: () => void;
}

export const useDeleteTimetablePrompt = ({
  timetableId,
  timetableName,
  genericErrorMessage,
  onDeleted,
}: UseDeleteTimetablePromptArgs) => {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { trackEvent, captureException } = useUserTracking();

  return useDeleteEntityPrompt({
    entityId: timetableId,
    canDelete: canDeleteTimetable,
    deleteEntity: deleteTimetable,
    isNotDeletableError: isTimetableNotDeletableError,
    genericErrorMessage,
    onDeleted: async () => {
      trackEvent('timetable_deleted', { timetableId, timetableName });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted “${timetableName}”.`,
      });
      onDeleted?.();
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.timetables] });
    },
    onCheckError: (err) => captureException(err, { tags: { action: 'delete_timetable_check', timetableId } }),
    onDeleteError: (err) => captureException(err, { tags: { action: 'delete_timetable', timetableId } }),
  });
};
